/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { ENDPOINTS } from '../endpoints';
import { safeAction, ActionResponse } from '../../lib/utils/safe-action';
import { AiSignal, SmartMoneyTrader, TokenAnalytics, AggregatedMarketData } from '../../types/ai';
import { AiEngine } from '../../lib/ai/engine';
import { raydiumApi } from '../raydium';
import { pythApi } from '../pyth';
import { getBirdeyeHistory } from '../../lib/solana/birdeye';

export const analyticsApi = {
    /**
     * Get active AI trading signals
     */
    getSignals: async (): Promise<ActionResponse<AiSignal[]>> => {
        return safeAction(async () => {
            // In production: return await fetch(ENDPOINTS.ANALYTICS.SIGNALS).then(r => r.json());
            return await AiEngine.scanForSignals();
        });
    },

    /**
     * Get Smart Money wallet activity
     */
    getSmartMoney: async (): Promise<ActionResponse<SmartMoneyTrader[]>> => {
        return safeAction(async () => {
            // In production: return await fetch(ENDPOINTS.ANALYTICS.SMART_MONEY).then(r => r.json());
            return await AiEngine.getSmartMoneyTraders();
        });
    },

    /**
     * Get deep analysis for a token
     */
    getTokenAnalysis: async (tokenMint: string): Promise<ActionResponse<TokenAnalytics>> => {
        return safeAction(async () => {
            // Mock price fetch for analysis
            const currentPrice = 1.0; 
            // We need a full TokenMetadata object for the engine, mocking for API consistency
            const mockToken: any = { 
                name: 'Token', 
                symbol: 'TKN', 
                properties: { mintAddress: tokenMint } 
            };
            return await AiEngine.analyzeToken(mockToken, currentPrice);
        });
    },

    /**
     * Get Aggregated Market Data from multiple sources (Raydium, Pyth, etc.)
     */
    getAggregatedTokenStats: async (tokenMint: string, symbol: string): Promise<ActionResponse<AggregatedMarketData>> => {
        return safeAction(async () => {
            const sources: ('Raydium' | 'Jupiter' | 'Pyth' | 'Birdeye' | 'Meteora')[] = [];
            
            // 1. Fetch Pyth Price
            const pythData = await pythApi.getPrice(symbol);
            if (pythData) sources.push('Pyth');

            // 2. Fetch Raydium Pool Stats
            const raydiumStats = await raydiumApi.getPoolStats(tokenMint);
            if (raydiumStats) sources.push('Raydium');

            // 3. Fetch Birdeye Data (Simulated or Real via lib)
            const history = await getBirdeyeHistory(tokenMint, '1D');
            if (history.length > 0) sources.push('Birdeye');

            // 4. Simulate Jupiter/Meteora aggregation
            sources.push('Jupiter');
            if (Math.random() > 0.5) sources.push('Meteora');

            // Aggregate Data
            const price = pythData?.price || raydiumStats.price || 0;
            const volume = raydiumStats.volume24h || 0;
            const liquidity = raydiumStats.tvl || 0;
            
            // Calculate change if history exists, else random or pyth
            let change24h = 0;
            if (history.length > 0) {
                const start = history[history.length - 1].o; // Oldest
                const end = history[0].c; // Newest
                if (start > 0) change24h = ((end - start) / start) * 100;
            } else {
                change24h = (Math.random() * 10) - 5;
            }

            return {
                price,
                liquidity,
                volume24h: volume,
                priceChange24h: change24h,
                holders: Math.floor(Math.random() * 5000) + 100, // Mock holder count until RPC integration
                supply: 1_000_000_000,
                sources,
                lastUpdated: Date.now()
            };
        });
    }
};